import { ScanSearch, FlaskConical, Network, GraduationCap, Compass, RefreshCcw, CheckCircle } from "lucide-react";
import useScrollReveal from "../hooks/useScrollReveal";
import { useLanguage } from "../i18n/LanguageContext";

const delivIcons = [ScanSearch, FlaskConical, Network, GraduationCap, Compass, RefreshCcw];

export default function AITransformation() {
  const [ref, isVisible] = useScrollReveal();
  const [ref2, isVisible2] = useScrollReveal();
  const { t } = useLanguage();
  const a = t.ai;

  return (
    <section id="ai-transformation" data-testid="ai-transformation-section" ref={ref} className="bg-[#0F1A2E] py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-1/3 left-0 w-[450px] h-[450px] bg-[#D4AF37]/[0.03] rounded-full blur-[110px] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(212,175,55,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.03) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20" style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? "translateY(0)" : "translateY(24px)", transition: "opacity 0.8s ease-out, transform 0.8s ease-out" }}>
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-12 bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-widest">{a.label}</span>
            </div>
            <h2 className="text-4xl md:text-5xl text-white leading-tight mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {a.heading}<br /><span className="gold-gradient-text">{a.headingAccent}</span>
            </h2>
            <p className="text-slate-300 text-base leading-relaxed max-w-lg">{a.intro}</p>
          </div>
          <div className="flex flex-col justify-center">
            <p className="text-slate-300 text-sm leading-relaxed mb-8">{a.approachIntro}</p>
            <p className="text-slate-300 text-[10px] uppercase tracking-widest mb-4">{a.whyLabel}</p>
            <ul className="space-y-3">
              {a.whyPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed"
                  style={{ opacity: isVisible ? 1 : 0, transition: "opacity 0.6s ease-out", transitionDelay: `${300 + i * 100}ms` }}>
                  <CheckCircle size={14} className="text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={2} />{point}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-16">
          <div className="h-px flex-1 bg-slate-800" />
          <span className="text-slate-600 text-[10px] uppercase tracking-widest">{a.divider}</span>
          <div className="h-px flex-1 bg-slate-800" />
        </div>

        <div ref={ref2} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-800/50">
          {a.deliverables.map((item, i) => {
            const Icon = delivIcons[i];
            return (
              <div key={i} data-testid={`ai-deliverable-${i}`}
                className="bg-[#162A45] p-8 group hover:bg-[#0F1A2E] transition-colors duration-400 relative overflow-hidden"
                style={{ opacity: isVisible2 ? 1 : 0, transform: isVisible2 ? "translateY(0) scale(1)" : "translateY(24px) scale(0.97)", transition: "opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1), background-color 0.4s", transitionDelay: `${i * 100}ms` }}>
                <span className="absolute top-4 right-5 text-slate-800 group-hover:text-slate-700/60 text-5xl font-bold transition-colors duration-400 select-none"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}>{String(i + 1).padStart(2, "0")}</span>
                <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                  <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-white text-base font-semibold mb-3 leading-snug pr-8">{item.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed group-hover:text-slate-300 transition-colors duration-300">{item.body}</p>
                <div className="absolute bottom-0 left-0 h-px w-0 bg-[#D4AF37] group-hover:w-full transition-all duration-500" />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
